import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";

import { CP_Packages } from "../../data/DemoData";
import { ShoppingBagIcon } from "../../assets/icons";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAppSelector } from "../../hooks/hooks";
import ProductCard from "./PaymentCard";
import FixedProductCard from "./PaymentCard";
import { PlanListsProps } from "./types";
import { formatCurrency } from "../../utils/utils";

const PlanLists = (props: PlanListsProps) => {
  const { data: appData } = useAppSelector((state) => state.appData);
  // Extract the last path segment as the category key
  const location = useLocation();
  const path = location.pathname;
  const segments = path.split("/").filter(Boolean); // ["products", "data", "MTN"]
  const formattedProduct =
    segments.length >= 2 ? segments[segments.length - 2] : ""; //data
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : ""; //MTN

  //get provider
  const provider = appData[formattedProduct]?.providers.find(
    (item: { name: string }) => item.name === formattedProvider
  );
  //get categories
  const categories = appData[formattedProduct]?.categories || [];
  const filteredCategories = categories.filter(
    (category: any) => category.provider_id === provider?.id
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<Number>(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  // Add state for inputs
  const [uniqueNumber, setUniqueNumber] = useState("");
  const [additionalInputValue, setAdditionalInputValue] = useState<number>(0);

  useEffect(() => {
    if (filteredCategories.length > 0) {
      setSelectedCategoryId(filteredCategories[0].id);
    }
  }, []);
  // Filter plans based on the selected category
  const plans =
    appData[formattedProduct]?.plans.filter(
      (plan: any) => plan.category_id === selectedCategoryId
    ) || [];

  const handlePlanClick = (plan: any) => {
    setSelectedPlan(plan);
  };

  function getOriginalPrice(discountedPrice: number, discountPercent: number) {
    const originalPrice = discountedPrice / (1 - discountPercent / 100);
    return originalPrice;
  }

  return (
    <div className=" rounded-lg border border-border-border1 dark:border-tabs w-full lg:w-[623px]">
      {/* Cards */}
      <div className="w-full dark:bg-[#FFFFFF1A] border-b border-border-1 dark:border-tabs">
        <p className="font-semibold dark:text-white p-4  text-sm lg:text-base ">
          Products
        </p>
      </div>
      <div className="p-4">
        {/* Categories Filter if more than 1 category */}
        {filteredCategories.length > 1 && (
          <div className="rounded-lg p-2 row-2 bg-[#F4F4F5] dark:bg-BG-dark w-full h-[42px] mt-2 mb-6">
            {filteredCategories.map((item: any, index: number) => (
              <button
                key={index}
                className={`flex-1 py-[6px] px-[6px] bp:px-[10px] w-[140px] bp:w-[153px] h-[34px] text-xs place-content-center ${
                  selectedCategoryId === item.id
                    ? "inter-xs-semibold-dark rounded-lg dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => {
                  setSelectedCategoryId(item.id);
                  setSelectedPlan(null);
                }}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}

        {/* PlanLists  */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((plan: any, index: number) => (
            <div
              key={index}
              className={`flex items-center justify-between p-2 border dark:bg-[#FFFFFF1A] dark:border-tabs rounded-lg lg:w-[279px] h-[56px] dark:text-white ${
                selectedPlan?.id === plan.id ? "bg-gray-200" : ""
              }`}
              onClick={() => handlePlanClick(plan)}
            >
              <div className="flex items-center gap-3">
                <img
                  src={plan.image_url}
                  alt={plan.name}
                  className="w-8 h-8 bp:w-12 bp:h-12"
                />
                <div>
                  <p className="text-xs bp:text-sm font-bold">{plan.name}</p>
                  <div className="flex items-center justify-between gap-1 bp:gap-2">
                    <div className="bg-[#039EF2] text-white  px-2 py-1 rounded-2xl gap-1 flex items-center">
                      <div className="w-3 h-2  flex items-center">
                        <img
                          src="/PNGS/Coins.png"
                          alt="Coins"
                          className="w-[20px] h-[20px]"
                        />
                      </div>
                      <span className=" text-[10px] bp:text-xs font-inter font-semibold">
                        {formatCurrency(plan.wallet_price)}
                      </span>
                    </div>
                    <p className="text-black bp:text-xs text-[10px] dark:text-white">
                      ₦{formatCurrency(plan.wallet_price)}
                    </p>
                    {plan.discount > 0 && (
                      <p className="text-gray-500 bp:text-xs text-[10px] line-through">
                        {getOriginalPrice(
                          plan.wallet_price,
                          plan.discount
                        ).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {plan.discount > 0 && (
                <span className=" bg-[#FEF3F2] rounded-xl">
                  <p className="text-[#D92D20] text-xs font-semibold font-satoshi px-[2px] py-[2px]">
                    {formatCurrency(plan.discount)}% off
                  </p>
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Recipient Details */}

        {/* display recipient details only when a plan has been selected */}
        {selectedPlan ? (
          <div className="mt-4 border  border-border-border1 dark:border-tabs rounded p-3 dark:bg-[#FFFFFF1A]">
            <p className="text-xs bp:text-base font-satoshi font-semibold text-black dark:text-white">
              {props.recipientLabel || "Recipient Details"}
            </p>
            <input
              type="text"
              name="unique_number"
              placeholder={props.recipientLabel}
              className="w-full mt-8 p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              value={uniqueNumber}
              onChange={(e) => setUniqueNumber(e.target.value)}
            />

            {/* Additional Input*/}
            {props.additionalInput && (
              <>
                <p className="text-xs bp:text-base font-satoshi font-semibold text-black dark:text-white">
                  {props.additionalInputLabel || "Recipient Details"}
                </p>
                <input
                  name={props.additionalInputName}
                  type={props.additionalInputType}
                  placeholder={props.additionalInputPlaceholder}
                  className="w-full mt-4 p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                  value={additionalInputValue || ""}
                  onChange={(e) => {
                    const value = parseInt(
                      e.target.value.replace(/[^0-9]/g, ""),
                      10
                    );
                    setAdditionalInputValue(isNaN(value) ? 0 : value);
                  }}
                />
              </>
            )}

            <div className=" mt-2 bg-[#E6F5FE] rounded-3xl">
              <div className="flex items-center p-2 bp:p-4">
                <input type="checkbox" className="mr-2 text-xl" />
                <span className=" text-[10px] bp:text-sm text-gray-500 font-satoshi dark:bg-[#E6F5FE]">
                  {`Save my ${props.recipientLabel} details for next time.`}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {" "}
            <p className="text-xs text-center bp:text-base font-satoshi font-semibold text-black dark:text-white">
              Select a product!
            </p>
          </div>
        )}

        {/* Payment Card */}
        <FixedProductCard
          selectedPlan={selectedPlan}
          uniqueNumber={uniqueNumber}
          type={formattedProduct}
          additionalInputValue={additionalInputValue}
        />
      </div>
    </div>
  );
};

export default PlanLists;
